import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { toast } from "../components/common/toast";
import { showEntitlementToast } from "../components/common/EntitlementToast";
import { clearCurrentUserCache } from "../auth/auth.service";
import { loginPathFor } from "../auth/returnTo";
import {
  describePermissionDenial,
  notePermissionDenialShown,
  permissionToastSuppressed,
  shouldShowPermissionToast,
} from "../utils/permissionError";

let loader: any = null;

export const registerLoader = (l: any) => {
  loader = l;
};

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,

  headers: {
    "Content-Type": "application/json",

    Accept: "application/json, text/plain, */*",

    "Access-Control-Allow-Origin": "*",

    "x-requestid": "00000000-0000-0000-0000-000000000001",
  },

  timeout: 30000,
});

/**
 * A request for a later page of a paged search — what infinite scroll sends
 * as a list is scrolled. Those skip the full-screen loader: the list shows
 * its own "Loading more…" at the bottom, and blanking the screen on every
 * scroll step made the list unusable while it grew.
 */
const isLoadMoreRequest = (data: unknown): boolean => {
  let body: unknown = data;
  if (typeof data === "string") {
    try {
      body = JSON.parse(data);
    } catch {
      return false;
    }
  }
  const pageIndex = (body as { pageIndex?: unknown } | null)?.pageIndex;
  return typeof pageIndex === "number" && pageIndex > 0;
};

type QuietConfig = { __skipGlobalLoader?: boolean };

/**
 * Reads — a GET, or a POST to a search / lookup endpoint — only cover the
 * screen if they are still running after this long. Most finish well inside
 * it, and the pages already show their own loading state; the overlay
 * flashing up on every search was what made pages feel slow. Writes still
 * block the screen at once, so a save can't be clicked twice.
 */
const READ_LOADER_DELAY_MS = 400;
const READ_ENDPOINT =
  /\/(search|getmedialink|summary|getcafs|getcount|snapshot|getauditlog)(\/|\?|$)/i;

const isReadRequest = (config: { method?: string; url?: string }): boolean =>
  (config.method ?? "get").toLowerCase() === "get" ||
  READ_ENDPOINT.test(config.url ?? "");

/**
 * Identical reads already on the wire are shared rather than sent again.
 *
 * Separate parts of a page often ask for the same thing at the same moment —
 * the header and the sidebar both counting unread notifications, two hooks
 * resolving the same connected contracts, the header and a page both loading
 * the signed-in member. Each used to be its own request. A read whose method,
 * URL, body (key order ignored) and signed-in user all match one still in
 * flight now waits for that one; every caller gets its own copy of the raw
 * response, parsed separately, so none can change another's data.
 *
 * Only in-flight requests are shared — nothing is cached once answered, so a
 * read after a save always sees the save. Writes, requests that can be
 * cancelled, and bodies that aren't JSON (uploads) always go out on their own.
 */
const inFlightReads = new Map<string, Promise<AxiosResponse>>();

const sortKeys = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(sortKeys)
    : value && typeof value === "object"
      ? Object.fromEntries(
          Object.keys(value as Record<string, unknown>)
            .sort()
            .map((k) => [k, sortKeys((value as Record<string, unknown>)[k])]),
        )
      : value;

/** The body as a stable string, or null when it can't be compared. */
const comparableBody = (data: unknown): string | null => {
  if (data == null || data === "") return "";
  if (typeof data !== "string") return null;
  try {
    return JSON.stringify(sortKeys(JSON.parse(data)));
  } catch {
    return data;
  }
};

const sendRequest = axios.getAdapter(api.defaults.adapter);

api.defaults.adapter = (config: InternalAxiosRequestConfig) => {
  const body = comparableBody(config.data);
  if (
    !isReadRequest(config) ||
    config.signal ||
    config.cancelToken ||
    body === null
  ) {
    return sendRequest(config);
  }

  const key = [
    (config.method ?? "get").toLowerCase(),
    axios.getUri(config),
    String(config.headers?.Authorization ?? ""),
    body,
  ].join("\n");

  let shared = inFlightReads.get(key);
  if (!shared) {
    shared = sendRequest(config).finally(() => inFlightReads.delete(key));
    inFlightReads.set(key, shared);
  }

  // A fresh response (and error) per caller, carrying the caller's own
  // config: the interceptors read their loader flags from it.
  return shared.then(
    (response) => ({ ...response, config }),
    (error) => {
      throw Object.assign(
        Object.create(Object.getPrototypeOf(error)),
        error,
        { config },
      );
    },
  );
};

/** >0 while a background refresh is running (see withQuietRequests). */
let quietDepth = 0;

/**
 * Runs `fn` with the full-screen loader suppressed for the requests it makes:
 * background refreshes (auto-refresh on Home/Dashboard, a live update on a
 * contract page) must not blank the screen the user is reading. Requests
 * that happen to start from elsewhere during the same window are quiet too,
 * which only costs them the overlay, never the request.
 */
export async function withQuietRequests<T>(fn: () => Promise<T>): Promise<T> {
  quietDepth += 1;
  try {
    return await fn();
  } finally {
    quietDepth -= 1;
  }
}

/**
 * REQUEST INTERCEPTOR
 */
api.interceptors.request.use(
  (config) => {
    const quiet = quietDepth > 0 || isLoadMoreRequest(config.data);
    (config as QuietConfig).__skipGlobalLoader = quiet;
    if (!quiet) {
      loader?.show?.(undefined, {
        delayMs: isReadRequest(config) ? READ_LOADER_DELAY_MS : 0,
      });
    }

    const token = localStorage.getItem("accessToken");

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },

  (error) => {
    loader?.hide?.();

    return Promise.reject(error);
  },
);

/**
 * RESPONSE INTERCEPTOR
 */
api.interceptors.response.use(
  (response) => {
    // Hidden only by requests that showed it, so the loader's show/hide
    // count stays balanced.
    if (!(response.config as QuietConfig)?.__skipGlobalLoader) {
      loader?.hide?.();
    }

    return response;
  },

  async (error) => {
    if (!(error?.config as QuietConfig | undefined)?.__skipGlobalLoader) {
      loader?.hide?.();
    }

    /**
     * HANDLE 401
     */
    if (error.response?.status === 401) {
      /**
       * CLEAR TOKENS
       */
      // The session is over; drop the memoised /Auth/me answer with it.
      clearCurrentUserCache();

      localStorage.removeItem("accessToken");

      localStorage.removeItem("refreshToken");

      localStorage.removeItem("organisation");

      localStorage.removeItem("companyName");

      if (window.location.pathname !== "/login") {
        // The session expired mid-page; after signing in again, come back here.
        window.location.href = loginPathFor(window.location.pathname + window.location.search);
      }
    }

    /**
     * HANDLE 402
     *
     * The licensing engine refused: a hard limit was hit, the plan leaves
     * the module out, or the subscription is inactive. The body names the
     * meter, the figures and what the user can do about it — surface that
     * instead of a generic failure, and let the licensing provider refresh.
     */
    if (error.response?.status === 402 && error.response?.data?.code) {
      error.isEntitlementDenied = true;
      error.entitlement = error.response.data;
      showEntitlementToast(error.response.data);
    }

    /**
     * HANDLE 403
     *
     * The API answers a denied PermissionAuthorize check with an empty 403,
     * so without this the user saw nothing, or a generic "Failed to …" that
     * reads like an outage rather than a missing permission. Describe the
     * attempt from the request URL, surface it once, and hang the message on
     * the error so call sites can reuse it instead of their own wording.
     */
    if (error.response?.status === 403) {
      const message = describePermissionDenial(error.config?.url);

      error.isPermissionDenied = true;
      error.permissionMessage = message;

      if (!permissionToastSuppressed() && shouldShowPermissionToast(message)) {
        toast.error(message, 5000);
      }

      // Armed whether or not this particular denial was shown, so the
      // caller's generic failure toast is dropped either way.
      notePermissionDenialShown();
    }

    return Promise.reject(error);
  },
);
